import React from 'react';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { DndProvider, TouchTransition, MouseTransition } from 'react-dnd-multi-backend';
import I18N from '@leverege/i18n';
import { Pane, Flex, Popup, Label, NumericInput, Text, TextInput, Checkbox } from '@leverege/ui-elements';
import { useInstanceCallback, useValueChange } from '@leverege/ui-hooks';
import DashboardContext, { useDashboardContext } from "../shared/DashboardContext.js";
import GridEditor from "./GridEditor.js";
import CardGrid from "./CardGrid.js";
import { CardTitle } from "../shared/index.js";
import { Dashboard } from "../model/index.js";
import CardSelector from "./CardSelector.js";
const {
  t
} = I18N.ns('uiDashboard.editor.DashboardEditor');
const HTML5toTouch = {
  backends: [{
    id: 'html5',
    backend: HTML5Backend,
    transition: MouseTransition
  }, {
    id: 'touch',
    backend: TouchBackend,
    options: {
      enableMouseEvents: true
    },
    preview: true,
    transition: TouchTransition
  }]
};
function DashboardEditor(props) {
  const {
    models,
    value,
    sharedProps,
    editors,
    viewers,
    optionEditors,
    eventData,
    onChange,
    style,
    className,
    options,
    variant = 'dashboardEditor',
    variants,
    addWidgetText,
    deferEditing,
    objectType,
    getModelsFor,
    getModelCreators
  } = props;
  const dashboardContext = useDashboardContext();
  const {
    columns: maxColumns
  } = dashboardContext;
  const onModelChange = useValueChange(Dashboard, props);
  const [onAdd] = useInstanceCallback([CardGrid.addCard, CardGrid.removeCard], {
    ...props,
    dashboardContext
  });
  return /*#__PURE__*/React.createElement("div", {
    style: style
  }, /*#__PURE__*/React.createElement(DndProvider, {
    options: HTML5toTouch
  }, /*#__PURE__*/React.createElement(DashboardContext, {
    variants: variants,
    addWidgetText: addWidgetText,
    models: models,
    editors: editors,
    viewers: viewers,
    optionEditors: optionEditors,
    sharedProps: sharedProps,
    deferEditing: deferEditing,
    objectType: objectType,
    getModelsFor: getModelsFor,
    getModelCreators: getModelCreators,
    depth: 0,
    options: options
  }, /*#__PURE__*/React.createElement(Pane, {
    variant: variant,
    style: style,
    className: className
  }, /*#__PURE__*/React.createElement(CardTitle, {
    title: t('title')
  }, /*#__PURE__*/React.createElement(Flex, {
    style: {
      flexGrow: 1
    }
  }, /*#__PURE__*/React.createElement(Popup, {
    variant: "dashboardEditorPopup|dashboardCardEditorPopup",
    title: /*#__PURE__*/React.createElement("i", {
      className: "fa fa-gear"
    })
  }, /*#__PURE__*/React.createElement(Flex, {
    variant: "colL"
  }, /*#__PURE__*/React.createElement(LayoutEditor, {
    value: value,
    onChange: onModelChange,
    maxColumns: maxColumns
  }))), /*#__PURE__*/React.createElement(CardSelector, {
    onSelect: onAdd
  }))), /*#__PURE__*/React.createElement(GridEditor, {
    value: value,
    eventData: eventData,
    onChange: onChange,
    onAdd: onAdd
  })))));
}
export default /*#__PURE__*/React.memo(DashboardEditor);
function LayoutEditor(props) {
  const {
    onChange,
    value,
    maxColumns
  } = props;
  return /*#__PURE__*/React.createElement(Pane, {
    variant: "padding"
  }, /*#__PURE__*/React.createElement(Flex, {
    variant: "colM",
    align: "stretch"
  }, /*#__PURE__*/React.createElement(Text, {
    variant: "title"
  }, t('gridOptions')), /*#__PURE__*/React.createElement(Flex, {
    variant: "rowM",
    align: "center",
    justify: "space-between"
  }, /*#__PURE__*/React.createElement(Label, {
    variant: "dashboardCardEditor|small"
  }, t('columns')), /*#__PURE__*/React.createElement(NumericInput, {
    variant: "dashboardCardEditor|small",
    size: 2,
    value: value.columns || 1,
    eventData: "setColumns",
    min: 1,
    max: maxColumns,
    onChange: onChange
  })), /*#__PURE__*/React.createElement(Flex, {
    variant: "rowM",
    align: "center",
    justify: "space-between"
  }, /*#__PURE__*/React.createElement(Label, {
    variant: "dashboardCardEditor|small"
  }, t('columnWidth')), /*#__PURE__*/React.createElement(TextInput, {
    variant: "dashboardCardEditor|small",
    size: 8,
    value: value.columnSize || '1fr',
    eventData: "setColumnSize",
    min: 1,
    onChange: onChange
  })), /*#__PURE__*/React.createElement(Flex, {
    variant: "rowM",
    align: "center",
    justify: "space-between"
  }, /*#__PURE__*/React.createElement(Checkbox, {
    label: t('showOptions'),
    variant: "dashboardCardEditor|small",
    value: Dashboard.getShowOptions(value),
    eventData: "setShowOptions",
    onChange: onChange
  }))));
}